import { useState } from 'react';

import * as yup from 'yup';

/**
 * 타입 정의: 스키마가 고정된 형태 또는 동적으로 생성되는 형태
 */
type StaticSchema<T extends yup.AnyObject> = yup.ObjectSchema<T>;
type DynamicSchema<T extends yup.AnyObject, V> = (validateOptions: V) => yup.ObjectSchema<T>;

/**
 * 반환 타입 정의
 */
type UseValidationReturn<T, V = undefined> = {
  errors: Partial<Record<keyof T, string>>;
  validate: (values: T, validateOptions?: V) => Promise<boolean>;
  resetErrors: () => void;
};

/**
 * 함수 오버로드 선언
 */
function useValidation<T extends yup.AnyObject>(schema: StaticSchema<T>): UseValidationReturn<T>;
function useValidation<T extends yup.AnyObject, V>(schema: DynamicSchema<T, V>): UseValidationReturn<T, V>;
/**
 * 유효성 검사를 수행하고 에러 상태를 관리하는 커스텀 훅
 *
 * @param schema - yup 유효성 검사 스키마
 * @param initialValues - 초기 입력 값
 * @returns {object} - 입력 값, 에러 상태, 입력 핸들러, 검증 함수
 */
function useValidation<T extends yup.AnyObject, V = undefined>(schema: StaticSchema<T> | DynamicSchema<T, V>) {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const isDynamicValidation = typeof schema === 'function';

  const resetErrors = () => {
    setErrors({});
  };
  /**
   * 입력 값을 검증하고 에러 상태를 업데이트
   *
   * @param values - 검증할 값
   * @param validateOptions - 동적 스키마에 필요한 옵션
   * @returns {boolean} - 유효성 검사를 통과하면 true, 아니면 false
   */
  const validate = async (values: T, validateOptions?: V): Promise<boolean> => {
    try {
      if (isDynamicValidation && validateOptions !== undefined) {
        await schema(validateOptions).validate(values, { abortEarly: false });
      } else if (!isDynamicValidation) {
        await (schema as StaticSchema<T>).validate(values, { abortEarly: false });
      } else {
        throw new Error('validateOptions is required for dynamic validation');
      }

      resetErrors();
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: Partial<Record<keyof T, string>> = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof T] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  return { errors, validate, resetErrors };
}

export default useValidation;
